/**
 * React hooks for checking permissions on the client side.
 *
 * Reads role and overrides from the centralized permission store,
 * then evaluates permissions using the permission matrix.
 *
 * @example
 * ```tsx
 * const { result, isAllowed, needsApproval, isDenied, isLoading } = usePermission(
 *   'dispenses:create'
 * )
 *
 * if (isAllowed) { // render create button }
 * if (needsApproval) { // render button that triggers approval modal }
 * if (isDenied) { // hide or disable }
 * ```
 */

import * as React from "react";
import type { Permission, PermissionResult, Role } from "@/lib/permissions";
import { usePermissionStore } from "@/lib/stores/permission-store";
import { useAuth } from "@/lib/auth/auth-provider";

// -------------------------------------------------------
// Cache helpers (delegate to store)
// -------------------------------------------------------

/** Clear the cached role and overrides (e.g. on user switch). */
export function clearPermissionCache() {
  usePermissionStore.getState().reset();
}

/** Clear and re-fetch overrides from the API (e.g. after admin saves new overrides). */
export function clearOverridesCache() {
  usePermissionStore.getState().refetchOverrides();
}

// -------------------------------------------------------
// Hook
// -------------------------------------------------------

interface UsePermissionOptions {
  /** Skip the permission check (e.g. when context isn't ready yet). */
  skip?: boolean;
}

interface UsePermissionReturn {
  /** Full permission result from checkPermission(). */
  result: PermissionResult | null;
  /** True if the action is allowed without approval. */
  isAllowed: boolean;
  /** True if the action requires another user's approval. */
  needsApproval: boolean;
  /** True if the action is fully denied. */
  isDenied: boolean;
  /** True while the permission store is not yet loaded. */
  isLoading: boolean;
  /** The resolved role of the active user. */
  role: Role | null;
}

export function usePermission(
  permission: Permission,
  options?: UsePermissionOptions
): UsePermissionReturn {
  const auth = useAuth();
  const role = usePermissionStore((s) => s.role);
  const isLoaded = usePermissionStore((s) => s.isLoaded);
  const check = usePermissionStore((s) => s.check);
  const overrides = usePermissionStore((s) => s.overrides);
  const skip = options?.skip ?? false;

  const result = React.useMemo<PermissionResult | null>(() => {
    if (skip || !role) return null;
    return check(permission);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, permission, skip, overrides]);

  const isLoading = auth.isLoading || !isLoaded;

  return {
    result,
    isAllowed: result?.type === "allowed",
    needsApproval: result?.type === "requires_approval",
    isDenied: result?.type === "denied",
    isLoading,
    role,
  };
}

// -------------------------------------------------------
// Overloaded helper: check multiple permissions at once
// -------------------------------------------------------

type UsePermissionsReturn = Record<
  string,
  {
    result: PermissionResult | null;
    isAllowed: boolean;
    needsApproval: boolean;
    isDenied: boolean;
  }
>;

/**
 * Check multiple permissions at once.
 *
 * @example
 * ```tsx
 * const perms = usePermissions(
 *   ['dispenses:create', 'dispenses:delete']
 * )
 * if (perms['dispenses:create'].isAllowed) { ... }
 * ```
 */
export function usePermissions(
  permissions: Permission[]
): UsePermissionsReturn & { isLoading: boolean; role: Role | null } {
  const auth = useAuth();
  const role = usePermissionStore((s) => s.role);
  const isLoaded = usePermissionStore((s) => s.isLoaded);
  const check = usePermissionStore((s) => s.check);
  const overrides = usePermissionStore((s) => s.overrides);

  const isLoading = auth.isLoading || !isLoaded;

  // Stable key for permissions array
  const permKey = permissions.join(",");

  const results = React.useMemo(() => {
    const out: UsePermissionsReturn = {};
    for (const perm of permissions) {
      if (!role) {
        out[perm] = {
          result: null,
          isAllowed: false,
          needsApproval: false,
          isDenied: false,
        };
      } else {
        const result = check(perm);
        out[perm] = {
          result,
          isAllowed: result?.type === "allowed",
          needsApproval: result?.type === "requires_approval",
          isDenied: result?.type === "denied",
        };
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, permKey, overrides]);

  return Object.assign(results, { isLoading, role });
}
